import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

const AVATAR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
  '#F368E0', '#48DBF8', '#A29BFE', '#FD79A8', '#FDCB6E',
  '#6C5CE7', '#00B894', '#00CEC9', '#FAB1A0', '#74B9FF',
];

const getColorFromString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const isDefaultPic = (url) => url && url.endsWith('default.png');

const getBackground = ({
  linearGradient, backgroundColor, name,
}) => {
  if (linearGradient?.length === 2) {
    return `linear-gradient(135deg, ${linearGradient[0]}, ${linearGradient[1]})`;
  }
  if (backgroundColor) return backgroundColor;
  if (name) return getColorFromString(name);
  return '#E5E5E5';
};

const getInitialsFontSize = ({ size, fontSize }) => {
  if (typeof fontSize === 'number') return fontSize;
  if (typeof size === 'number') {
    return Math.min(48, Math.max(12, Math.round(size * 0.4)));
  }
  return 16;
};

// The parent avatar container uses overflow hidden now, so offset absolute positioned elements
// like the edit button were partially hidden. To fix we created a position relative wrapper,
// and a non relative parent with overflow hidden. So absolute elements will position based on
// the relative wrapper, and not be hidden.

const AthleteAvatarWrapper = styled('div')`
  position: relative;
  height: auto;
  width: auto;
`;

const AthleteAvatarContainer = styled('div')`
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${(props) => (props.size ? `${props.size}px` : '35px')};
  width: ${(props) => (props.size ? `${props.size}px` : '35px')};
  min-width: ${(props) => (props.size ? `${props.size}px` : '35px')};
  border-width: ${(props) => (props.borderWidth ? `${props.borderWidth}px` : 'none')};
  border-style: ${(props) => (props.borderWidth ? 'solid' : 'none')};
  border-color: ${(props) => (props.borderColor ? `${props.borderColor}` : 'none')};
  background: ${(props) => getBackground(props)};
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: ${(props) => `${getInitialsFontSize(props)}px`};
  line-height: 1;
  color: #ffffff;
  flex-shrink: 0;

  img {
    ${(props) => (!props.scaled ? (
    `height: 100%;
      width: 100%;`
  ) : (
    'height: 70%;'
  ))}
    object-fit: cover;
  }
`;

const LinearGradientContainer = styled('div')`
  display: flex;
  background-image: ${(props) => (
    props.linearGradient ? `linear-gradient(45deg,${props.linearGradient[0]}, ${props.linearGradient[1]})` : 'none'
  )};
  height: ${(props) => (props.size ? `${props.size + 10}px` : '95px')};
  width: ${(props) => (props.size ? `${props.size + 10}px` : '95px')};
  border-radius: 50%;
  justify-content: center;
  align-items: center;
`;

const Avatar = ({
  avatar,
  alt,
  name,
  size,
  fontSize,
  backgroundColor,
  borderWidth,
  borderColor,
  linearGradient,
  showGradientRing,
  children,
  scaled,
}) => {
  const resolvedAvatar = isDefaultPic(avatar) ? null : avatar;
  const resolvedAlt = alt || name || 'avatar';
  const showImage = !!resolvedAvatar;
  const initials = !showImage ? getInitials(name) : null;

  const containerContent = (
    <AthleteAvatarContainer
      size={size}
      borderWidth={borderWidth}
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      linearGradient={!showImage ? linearGradient : null}
      name={name}
      scaled={scaled}
      fontSize={fontSize}
    >
      {showImage && <img src={resolvedAvatar} alt={resolvedAlt} />}
      {!showImage && initials}
      {children}
    </AthleteAvatarContainer>
  );

  if (showGradientRing && linearGradient) {
    return (
      <AthleteAvatarWrapper>
        <LinearGradientContainer linearGradient={linearGradient} size={size}>
          {containerContent}
        </LinearGradientContainer>
      </AthleteAvatarWrapper>
    );
  }

  return (
    <AthleteAvatarWrapper>
      {containerContent}
    </AthleteAvatarWrapper>
  );
};

Avatar.propTypes = {
  avatar: PropTypes.string,
  alt: PropTypes.string,
  name: PropTypes.string,
  children: PropTypes.element,
  size: PropTypes.number,
  fontSize: PropTypes.number,
  scaled: PropTypes.bool,
  backgroundColor: PropTypes.string,
  borderWidth: PropTypes.number,
  borderColor: PropTypes.string,
  linearGradient: PropTypes.arrayOf(PropTypes.string),
  showGradientRing: PropTypes.bool,
};

Avatar.defaultProps = {
  avatar: null,
  alt: null,
  name: null,
  children: null,
  size: null,
  fontSize: null,
  backgroundColor: null,
  borderWidth: null,
  borderColor: null,
  linearGradient: null,
  showGradientRing: false,
  scaled: false,
};

export default Avatar;
